/**
 * Task list statistics
 *
 * Pure derivation of per-list task statistics, shared by the todo service
 * and store.
 *
 * @module features/todo-list/utils/listStats
 */

import type { Task } from '../types/task.types';

/**
 * Get today's date as a YYYY-MM-DD string (local time).
 */
function today(): string {
  // Swedish locale gives a stable YYYY-MM-DD format
  return new Date().toLocaleDateString('sv-SE');
}

/**
 * Stats for a task list
 */
export interface ListStats {
  readonly total: number;
  readonly completed: number;
  readonly pending: number;
  readonly overdue: number;
  readonly percentage: number;
}

/**
 * Calculate list statistics from tasks array
 */
export function calculateListStats(
  tasks: readonly Task[],
  listId: string
): ListStats {
  const listTasks = tasks.filter((t) => t.listId === listId);
  const todayStr = today();
  const completed = listTasks.filter((t) => t.status === 'completed').length;
  const pending = listTasks.filter(
    (t) => t.status === 'pending' || t.status === 'in-progress'
  ).length;
  const overdue = listTasks.filter(
    (t) =>
      t.status !== 'completed' &&
      t.status !== 'cancelled' &&
      t.dueDate &&
      t.dueDate < todayStr
  ).length;

  return {
    total: listTasks.length,
    completed,
    pending,
    overdue,
    percentage:
      listTasks.length > 0
        ? Math.round((completed / listTasks.length) * 100)
        : 0,
  };
}
